/**
 * What the Auto-Reminders panel should render for "Next".
 *
 * A `client_reminders` row keeps `is_active = true` and a future `next_send_at`
 * even after its reminder has permanently stopped sending. That is deliberate on
 * the server: the 9 AM section pass `continue`s past a row it cannot send
 * (project not open — server/index.ts:567; no outstanding sent client tasks —
 * server/index.ts:581) WITHOUT mutating the row, so reopening a project resumes
 * its reminders untouched.
 *
 * The panel used to render `next_send_at` on the strength of `is_active` alone,
 * which meant a dead reminder advertised a confident send date indefinitely. As
 * of Aug 25 2026, 7 of the 11 active rows firm-wide were frozen this way — the
 * oldest since May 10 — and the resulting "will this actually send?" question is
 * what prompted this helper. Mirroring the server's first skip condition lets the
 * panel say WHY nothing will send instead of naming a date that will never fire.
 *
 * Deliberately mirrors only the project-status guard, not the task-count guard.
 * Every frozen row observed sits on a closed project, so this covers all of them;
 * duplicating the task predicate (client task AND `visible_to_emails` null AND
 * `sent_at` not null AND not done, including sub-tasks) risks drifting out of
 * sync with the server and claiming "paused" for a reminder that really will
 * send. A wrong pause is worse than a missing one.
 */

export type ReminderNextDisplay =
  /** Render nothing — matches the pre-existing `next_send_at && is_active` condition. */
  | { kind: "hidden" }
  /** Armed date exists but the pass will skip it; show the reason, not the date. */
  | { kind: "paused"; reason: string }
  /** Genuinely due to send at this timestamp. */
  | { kind: "next"; at: string };

export function reminderNextDisplay(
  reminder: { is_active: boolean; next_send_at: string | null } | null | undefined,
  project: { status: string; deleted_at?: string | null } | null | undefined,
): ReminderNextDisplay {
  if (!reminder?.is_active || !reminder.next_send_at) return { kind: "hidden" };

  // Unknown project: fall back to the old behavior rather than inventing a pause
  // we cannot justify.
  if (!project) return { kind: "next", at: reminder.next_send_at };

  if (project.deleted_at) return { kind: "paused", reason: "project deleted" };
  if (project.status !== "open") return { kind: "paused", reason: "project closed" };

  return { kind: "next", at: reminder.next_send_at };
}
